/**
 * 등록을 기다리는 화면 — 목록 한 줄을 사람이 읽는 말로 바꾼다(순수 계산).
 *
 * [화면 추가]에서 고르는 사람은 **눈앞에 그 기기가 없다.** 설치 기사는 현장에 있고,
 * 콘솔을 여는 사람은 사무실에 있다. 그래서 줄마다 "이게 내가 등록하려는 그 화면인가" 를
 * 가릴 단서가 있어야 한다 — 종류·해상도·주소·켜진 시각. 코드 여섯 자만 있으면 여러 대를
 * 한꺼번에 켜 둔 날 무엇을 고르는지 알 수 없다.
 *
 * 계산을 컴포넌트에서 떼어 낸 까닭은 **남은 시간** 때문이다. 코드는 15분이면 새것으로
 * 바뀌는데(`PAIRING_TTL_MIN`), 그 사실을 알려 주지 않으면 사람은 목록이 고장난 줄 안다 —
 * 방금 본 코드가 다음 갱신에서 다른 코드로 바뀌어 있기 때문이다.
 */

/** 서버가 주는 한 줄. `GET /api/v1/pairing/pending`. */
export interface PendingPairing {
  code: string;
  platform: string;
  screen_w: number | null;
  screen_h: number | null;
  ip: string;
  created_at: string;
  expires_at: string;
}

export interface PendingPairingView {
  code: string;
  /** "안드로이드 · 3840×2160 · 192.168.0.40" — 빈 값은 빠진다. */
  detail: string;
  /** "12분 뒤 새 코드" · "곧 새 코드" · "만료됨" */
  remaining: string;
  /** 1분도 남지 않았다. 고르는 순간 이미 바뀌었을 수 있다. */
  expiring: boolean;
}

/**
 * 셸이 보고한 플랫폼 이름을 사람 말로.
 *
 * 모르는 값은 **그대로 보여 준다.** 새 셸이 생겼을 때 목록에서 "알 수 없음" 이 늘어나는
 * 것보다, 낯선 이름이라도 그대로 보이는 편이 현장에서 가려내는 데 낫다.
 */
export function platformLabel(platform: string): string {
  const key = platform.trim().toLowerCase();
  switch (key) {
    case 'android':
      return '안드로이드';
    case 'windows':
      return '윈도우';
    case 'linux':
      return '리눅스';
    case 'macos':
    case 'darwin':
      return '맥';
    case 'tvos':
      return 'Apple TV';
    case 'web':
      return '브라우저';
    case '':
    case 'unknown':
      return '알 수 없음';
    default:
      return platform.trim();
  }
}

/** "3840×2160". 한쪽이라도 없으면 빈 문자열 — 없는 값을 0 으로 그리지 않는다. */
export function resolutionLabel(width: number | null, height: number | null): string {
  if (!width || !height) return '';
  return `${width}×${height}`;
}

/**
 * 남은 시간.
 *
 * **"몇 분 남음" 이 아니라 "몇 분 뒤 새 코드" 라고 말한다.** 남았다는 말은 놓치면 끝난다는
 * 뜻으로 들리는데, 실제로는 화면이 새 코드를 받아 계속 기다린다(`runtime.ts` 의 pairingFlow).
 * 서두르게 만들 이유가 없다 — 알려야 하는 것은 **지금 보는 여섯 자가 곧 옛것이 된다**는 사실뿐이다.
 */
export function remainingLabel(expiresAt: string, now: Date): { text: string; expiring: boolean } {
  const until = Date.parse(expiresAt);
  if (Number.isNaN(until)) return { text: '', expiring: false };

  const seconds = Math.round((until - now.getTime()) / 1000);
  if (seconds <= 0) return { text: '만료됨', expiring: true };
  if (seconds < 60) return { text: '곧 새 코드', expiring: true };
  return { text: `${Math.floor(seconds / 60)}분 뒤 새 코드`, expiring: false };
}

export function describePending(row: PendingPairing, now: Date): PendingPairingView {
  const remaining = remainingLabel(row.expires_at, now);
  const detail = [platformLabel(row.platform), resolutionLabel(row.screen_w, row.screen_h), row.ip.trim()]
    .filter((part) => part !== '')
    .join(' · ');

  return { code: row.code, detail, remaining: remaining.text, expiring: remaining.expiring };
}

/**
 * 목록에서 고른 화면의 기본 이름 제안.
 *
 * 이름을 비워 두면 서버가 `화면 <코드>` 로 짓는다(`services/pairing.ts`). 코드는 15분이면
 * 사라지는 값이라 **이름으로 남기면 아무 뜻도 없는 이름이 영원히 남는다.** 그래서 고른
 * 줄의 단서로 채워 준다 — 사람이 지우고 제 이름을 쓰면 그만이다.
 */
export function suggestedName(row: PendingPairing): string {
  const parts = [platformLabel(row.platform), resolutionLabel(row.screen_w, row.screen_h)].filter(
    (part) => part !== '' && part !== '알 수 없음',
  );
  return parts.length > 0 ? `새 화면 (${parts.join(' ')})` : '';
}
